import React, { useState, useRef } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import GenericListUI, { FetchParams, FetchResult } from '../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../components/CustomList/GenericDataTable';
import NotificationFormPopup from './NotificationFormPopup';
import { getPushRecordList } from '../../../lib/api/admin/adminApi';
import dayjs from 'dayjs';

// 알림 데이터 타입 정의 (API 응답 구조에 맞춤)
interface NotificationData {
  no: number;
  index: number;
  title: string;
  body: string;
  createBy: string;
  createAt: string;
  adminName: string;
}

const NotificationManagementPage: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);
  
  // 팝업 상태
  const [isFormPopupOpen, setIsFormPopupOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);

  // 컬럼 정의
  const columns: ColumnDefinition<NotificationData>[] = [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value) => value?.toString() || '',
    },
    {
      header: '생성일시',
      accessor: 'createAt' as keyof NotificationData,
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '제목',
      accessor: 'title',
      formatter: (value) => value || '-',
    },
    {
      header: '내용',
      accessor: 'body',
      formatter: (value) => {
        const content = value || '';
        return content.length > 50 ? content.substring(0, 50) + '...' : content;
      },
    },
    {
      header: '발송자',
      accessor: 'adminName',
      formatter: (value) => value || '-',
    },
    {
      header: '작성자 ID',
      accessor: 'createBy',
      formatter: (value) => value || '-',
    },
  ];

  // 실제 API를 호출하여 알림 데이터를 가져오는 함수
  const fetchNotificationData = async (params: FetchParams): Promise<FetchResult<NotificationData>> => {
    try {
      devLog('fetchNotificationData API 호출:', params);
      
      // 실제 API 호출
      const response = await getPushRecordList({
        fromDate: params.fromDate || '2025-01-01',
        toDate: params.toDate || '2025-12-31',
      });

      // API 응답에서 데이터 추출
      const apiData: NotificationData[] = response.data?.data || [];
      
      // 키워드 검색 필터링
      let filteredData = apiData;
      if (params.keyword) {
        const keyword = params.keyword.toLowerCase();
        filteredData = apiData.filter(item => 
          item.title.toLowerCase().includes(keyword) ||
          item.body.toLowerCase().includes(keyword) ||
          item.adminName.toLowerCase().includes(keyword) ||
          item.createBy.toLowerCase().includes(keyword)
        );
      }

      // 날짜 필터링 (API에서 이미 필터링되지만, 추가 클라이언트 필터링)
      if (params.fromDate && params.toDate) {
        const fromDate = dayjs(params.fromDate);
        const toDate = dayjs(params.toDate);
        filteredData = filteredData.filter(item => {
          const itemDate = dayjs(item.createAt);
          return itemDate.isAfter(fromDate) && itemDate.isBefore(toDate.add(1, 'day'));
        });
      }
      
      // API 응답을 FetchResult 형태로 변환
      const result: FetchResult<NotificationData> = {
        data: filteredData,
        totalItems: response.data?.metadata?.totalCnt || filteredData.length,
        allItems: response.data?.metadata?.allCnt || filteredData.length,
      };
      
      return result;
      
    } catch (error) {
      devError('알림 데이터 조회 에러:', error);
      
      // 에러 시 빈 결과 반환
      return {
        data: [],
        totalItems: 0,
        allItems: 0,
      };
    }
  };

  // 행 클릭 핸들러 - 알림 상세 정보 팝업 열기
  const handleRowClick = (item: NotificationData, index: number) => {
    setSelectedNotification(item);
    setIsFormPopupOpen(true);
  };

  // 새 알림 작성 버튼 클릭
  const handleNewNotification = () => {
    setSelectedNotification(null);
    setIsFormPopupOpen(true);
  };

  // 팝업 닫기
  const closeFormPopup = () => {
    setIsFormPopupOpen(false);
    setSelectedNotification(null);
  };

  // 알림 발송 후 목록 새로고침
  const handleNotificationSent = () => {
    setRefreshTrigger(prev => prev + 1);
    closeFormPopup();
  };

  return (
    <>
      <GenericListUI<NotificationData>
        ref={listRef}
        title="알림 관리"
        columns={columns}
        fetchData={fetchNotificationData}
        excelFileName="NotificationManagement"
        keyExtractor={(item, index) => {
          return item.index ? item.index.toString() : `notification-${index}`;
        }}
        enableSearch={true}
        searchPlaceholder="제목, 내용, 발송자, 작성자 ID로 검색"
        enableDateFilter={true}
        dateRangeOptions={['금월', '3개월', '6개월','지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
        addButton={{
          label: '새 알림 작성',
          onClick: handleNewNotification,
        }}
      />

      <NotificationFormPopup
        isOpen={isFormPopupOpen}
        notificationData={selectedNotification}
        onClose={closeFormPopup}
        onSent={handleNotificationSent}
      />
    </>
  );
};

export default NotificationManagementPage;
